import type { ElementType, ReactElement, ReactNode } from "react";
import { Children, cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type SequenceChildProps = {
  __terminalIndex?: number;
  __terminalActiveIndex?: number;
  __terminalStarted?: boolean;
  __terminalOnDone?: () => void;
};

function useInView<T extends HTMLElement>(enabled: boolean) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(!enabled);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) setInView(true);
      },
      { rootMargin: "100px" },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);

  return { ref, inView };
}

export type TerminalProps = {
  children: ReactNode;
  className?: string;
  sequence?: boolean;
  startOnView?: boolean;
  variant?: "brew" | "default";
  title?: string;
};

export function Terminal({
  children,
  className,
  sequence = true,
  startOnView = true,
  variant = "brew",
  title = "zsh",
}: TerminalProps) {
  const { ref, inView } = useInView<HTMLDivElement>(startOnView);
  const started = inView;

  const [activeIndex, setActiveIndex] = useState(0);

  // reset when (re)starting
  useEffect(() => {
    if (!sequence) return;
    if (!started) return;
    setActiveIndex(0);
  }, [sequence, started]);

  const childElements = useMemo(() => {
    const arr = Children.toArray(children);
    return arr.filter((c): c is ReactElement => isValidElement(c));
  }, [children]);

  const rendered = useMemo(() => {
    if (!sequence) return children;

    return childElements.map((el, idx) =>
      cloneElement(el as ReactElement<any>, {
        key: el.key ?? idx,
        __terminalIndex: idx,
        __terminalActiveIndex: activeIndex,
        __terminalStarted: started,
        __terminalOnDone: () => setActiveIndex((n) => Math.max(n, idx + 1)),
      }),
    );
  }, [children, sequence, childElements, activeIndex, started]);

  return (
    <div
      ref={ref}
      className={cn(
        "overflow-hidden rounded-lg border",
        variant === "brew"
          ? [
              "border-white/10 bg-zinc-950 text-zinc-100",
              "shadow-[0_14px_40px_rgba(0,0,0,0.22)]",
            ].join(" ")
          : "border-border/60 bg-card text-foreground",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 border-b px-4 py-2.5",
          variant === "brew" ? "border-white/10 bg-zinc-900/60" : "border-border/60 bg-muted/30",
        )}
      >
        <span className={cn("h-2.5 w-2.5 rounded-full", variant === "brew" ? "bg-red-500/70" : "bg-red-400/60")} />
        <span
          className={cn("h-2.5 w-2.5 rounded-full", variant === "brew" ? "bg-amber-400/70" : "bg-yellow-400/60")}
        />
        <span
          className={cn("h-2.5 w-2.5 rounded-full", variant === "brew" ? "bg-emerald-400/70" : "bg-green-400/60")}
        />
        <span
          className={cn(
            "ml-3 font-mono text-xs",
            variant === "brew" ? "text-zinc-300/80" : "text-muted-foreground",
          )}
        >
          {title}
        </span>
      </div>
      <div
        className={cn(
          "p-5 font-mono text-[13px] leading-relaxed",
          variant === "brew" ? "text-zinc-100/90" : "text-foreground/90",
        )}
      >
        <div className="flex flex-col gap-1 whitespace-pre overflow-x-auto">{rendered}</div>
      </div>
    </div>
  );
}

export type AnimatedSpanProps = {
  children: ReactNode;
  className?: string;
  delay?: number;
  startOnView?: boolean;
} & SequenceChildProps;

export function AnimatedSpan({
  children,
  className,
  delay = 0,
  __terminalIndex,
  __terminalActiveIndex,
  __terminalStarted,
  __terminalOnDone,
}: AnimatedSpanProps) {
  const isSequenced = __terminalIndex != null && __terminalActiveIndex != null;
  const isActive = !isSequenced || __terminalIndex === __terminalActiveIndex;
  const isPast = isSequenced && (__terminalIndex as number) < (__terminalActiveIndex as number);
  const started = __terminalStarted ?? true;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!started) return;
    if (isPast) {
      setVisible(true);
      return;
    }
    if (!isActive) return;

    const t = window.setTimeout(() => {
      setVisible(true);
      // allow a tiny time for the fade-in to be perceived
      window.setTimeout(() => __terminalOnDone?.(), 220);
    }, delay);

    return () => window.clearTimeout(t);
  }, [started, isActive, isPast, delay, __terminalOnDone]);

  return (
    <span
      className={cn(
        "transition-opacity duration-200",
        visible ? "opacity-100" : "opacity-0",
        className,
      )}
    >
      {children}
    </span>
  );
}

export type TypingAnimationProps = {
  children: string;
  className?: string;
  duration?: number; // ms per character
  delay?: number; // ms
  as?: ElementType;
  startOnView?: boolean;
} & SequenceChildProps;

export function TypingAnimation({
  children,
  className,
  duration = 18,
  delay = 0,
  as,
  __terminalIndex,
  __terminalActiveIndex,
  __terminalStarted,
  __terminalOnDone,
}: TypingAnimationProps) {
  const Comp: any = as ?? "span";
  const isSequenced = __terminalIndex != null && __terminalActiveIndex != null;
  const isActive = !isSequenced || __terminalIndex === __terminalActiveIndex;
  const isPast = isSequenced && (__terminalIndex as number) < (__terminalActiveIndex as number);
  const started = __terminalStarted ?? true;

  const [out, setOut] = useState(isPast ? children : "");

  useEffect(() => {
    if (!started) return;
    if (isPast) {
      setOut(children);
      return;
    }
    if (!isActive) return;

    setOut("");
    let i = 0;
    const startTimer = window.setTimeout(() => {
      const tick = () => {
        i += 1;
        setOut(children.slice(0, i));
        if (i >= children.length) {
          window.setTimeout(() => __terminalOnDone?.(), 120);
          return;
        }
        window.setTimeout(tick, duration);
      };
      tick();
    }, delay);

    return () => window.clearTimeout(startTimer);
  }, [started, isActive, isPast, children, duration, delay, __terminalOnDone]);

  return (
    <Comp className={cn(className)}>
      {out}
      {started && isActive && out.length < children.length ? (
        <span className="ml-[1px] inline-block w-[7px] animate-pulse opacity-60">▌</span>
      ) : null}
    </Comp>
  );
}

